import WriteButton from "@/components/WriteButton";
import SupplierAliasDialog from '@/components/SupplierAliasDialog';
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import { Card, CardHeader, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Truck, Phone, MapPin, PackageSearch, Search, FileSpreadsheet } from "lucide-react";

const stockText = (value) => Number(value || 0).toLocaleString("id-ID");
const moneyText = (value) => "Rp " + Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

import ReportActions from '@/components/ReportActions';

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [productsOpen, setProductsOpen] = useState(false);
  const [productData, setProductData] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [stockStore, setStockStore] = useState("total");
  const [stockStatus, setStockStatus] = useState("all");
  const [aliasProduct, setAliasProduct] = useState(null);

  const load = async () => {
    try { setItems((await api.get("/suppliers")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", phone: "", address: "" }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, phone: s.phone || "", address: s.address || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama supplier wajib diisi");
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success("Supplier disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus supplier "${s.name}"?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Supplier dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const showProducts = async (supplier) => {
    setProductsOpen(true); setProductsLoading(true); setProductsError(""); setProductSearch("");
    setStockStore("total"); setStockStatus("all");
    setProductData({ supplier, stores: [], products: [] });
    try { setProductData((await api.get(`/suppliers/${supplier.id}/products`)).data); }
    catch (e) { setProductsError(apiError(e)); }
    finally { setProductsLoading(false); }
  };

  const filteredProducts = useMemo(() => {
    const term = productSearch.trim().toLocaleLowerCase("id");
    return (productData?.products || []).filter((product) => {
      if (term && !`${product.name} ${product.barcode}`.toLocaleLowerCase("id").includes(term)) return false;
      const stock = stockStore === "total" ? Number(product.total_stock || 0) : Number(product.stock_by_store[stockStore] || 0);
      if (stockStatus === "available") return stock > 0;
      if (stockStatus === "empty") return stock <= 0;
      if (stockStatus === "low") return stock <= Number(product.min_stock || 0);
      return true;
    });
  }, [productData, productSearch, stockStore, stockStatus]);

  const exportProducts = async () => {
    if (!productData?.supplier?.id || exporting) return;
    setExporting(true); setProductsError("");
    try {
      const response = await api.get(`/suppliers/${productData.supplier.id}/products/export`, {
        params: { q: productSearch.trim(), store_id: stockStore, stock_status: stockStatus }, responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `produk_supplier_${productData.supplier.name.replace(/[^a-z0-9_-]+/gi, "_") || "supplier"}.xlsx`;
      link.click(); URL.revokeObjectURL(url);
    } catch (error) { setProductsError(apiError(error)); }
    finally { setExporting(false); }
  };

  return (
    <>
      <Card data-testid="supplier-management-card" className="min-w-0 bg-ui-card border-ui-line text-ui-text shadow-none">
      <CardHeader className="p-4 border-b border-ui-line">
        <ReportActions title="Daftar Supplier" headers={['Nama Supplier','Telepon','Alamat']} rows={items.map(s => [s.name,s.phone,s.address])} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Truck className="h-5 w-5 text-brand-readable" /> Supplier</h2>
            <CardDescription className="text-ui-muted mt-1">{items.length} pemasok untuk pembelian barang.</CardDescription>
          </div>
        <WriteButton data-testid="add-supplier-btn" onClick={openNew} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
          <Plus className="h-4 w-4" /> Tambah Supplier
        </WriteButton>
        </div>
      </CardHeader>

      <CardContent className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supplier-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`supplier-card-${s.id}`} className="rounded-xl border border-ui-line bg-ui-soft p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-accent-soft text-accent-readable flex items-center justify-center">
                    <Truck className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold">{s.name}</div>
                    {s.phone && <div className="text-xs text-ui-muted flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {s.phone}</div>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <WriteButton plain aria-label={`Edit supplier ${s.name}`} data-testid={`edit-supplier-${s.id}`} onClick={() => openEdit(s)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-ui-muted-surface flex items-center justify-center"><Pencil className="h-4 w-4 text-ui-muted" /></WriteButton>
                  <WriteButton plain aria-label={`Hapus supplier ${s.name}`} data-testid={`delete-supplier-${s.id}`} onClick={() => remove(s)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center"><Trash2 className="h-4 w-4 text-danger-readable" /></WriteButton>
                </div>
              </div>
              {s.address && <div className="text-xs text-ui-muted flex items-start gap-1 mt-3"><MapPin className="h-3 w-3 mt-0.5 shrink-0" /> {s.address}</div>}
              <Button data-testid={`supplier-products-${s.id}`} variant="outline" onClick={() => showProducts(s)} className="mt-4 w-full gap-2 border-ui-line">
                <PackageSearch className="h-4 w-4" /> Lihat Produk
              </Button>
            </div>
          ))}
          {items.length === 0 && <p className="text-ui-muted col-span-full text-center py-10">Belum ada supplier</p>}
        </div>
      </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-form-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Supplier" : "Tambah Supplier"}</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Data pemasok untuk input pembelian barang.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Nama Supplier</Label>
              <Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Telepon</Label>
              <Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Alamat</Label>
              <Input data-testid="supplier-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
            <WriteButton data-testid="save-supplier-btn" onClick={save} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">Simpan</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={productsOpen} onOpenChange={setProductsOpen}>
        <DialogContent data-testid="supplier-products-dialog" className="bg-ui-card border-ui-line text-ui-text w-[96vw] max-w-6xl max-h-[92dvh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Produk Supplier · {productData?.supplier?.name || "Supplier"}</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Produk berasal dari riwayat pembelian supplier. Stok ditampilkan per cabang dalam satuan dasar produk.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 lg:grid-cols-[minmax(220px,1fr)_220px_190px_auto] gap-2 shrink-0 items-end">
            <label className="text-xs space-y-1"><span>Cari produk</span><span className="relative block"><Search className="absolute left-3 top-3 h-4 w-4 text-ui-muted" /><Input data-testid="supplier-products-search" aria-label="Cari produk supplier" value={productSearch} onChange={(event) => setProductSearch(event.target.value)} placeholder="Nama / barcode…" className="pl-9 bg-ui-card border-ui-line" /></span></label>
            <label className="text-xs space-y-1"><span>Cabang stok</span><select data-testid="supplier-stock-store" value={stockStore} onChange={(event) => setStockStore(event.target.value)} className="theme-input w-full"><option value="total">Total semua cabang</option>{productData?.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
            <label className="text-xs space-y-1"><span>Kondisi stok</span><select data-testid="supplier-stock-status" value={stockStatus} onChange={(event) => setStockStatus(event.target.value)} className="theme-input w-full"><option value="all">Semua stok</option><option value="available">Stok tersedia</option><option value="empty">Stok habis</option><option value="low">Di bawah minimum</option></select></label>
            <Button data-testid="export-supplier-products" variant="outline" disabled={productsLoading || exporting || !productData?.supplier?.id} onClick={exportProducts} className="gap-2"><FileSpreadsheet className="h-4 w-4" />{exporting ? "Menyiapkan…" : "Export Excel"}</Button>
          </div>
          {productsError && <p role="alert" className="text-sm text-danger-readable">{productsError}</p>}
          <div className="min-h-0 overflow-auto rounded-xl border border-ui-line" role="region" aria-label="Daftar produk supplier, dapat digeser horizontal">
            <table data-testid="supplier-products-table" className="w-full min-w-[760px] text-sm whitespace-nowrap">
              <thead className="bg-ui-card text-ui-muted sticky top-0"><tr><th className="p-3 text-left">Produk</th>{productData?.stores.map((store) => <th key={store.id} className="p-3 text-right">{store.name}</th>)}<th className="p-3 text-right">Total</th><th className="p-3 text-left">Pembelian Terakhir</th></tr></thead>
              <tbody>
                {filteredProducts.map((product) => <tr key={product.id} data-testid={`supplier-product-${product.id}`} className="border-t border-ui-line">
                  <td className="p-3"><b>{product.name}</b><small className="block text-ui-muted">{product.barcode || "Tanpa barcode"} · {product.base_unit} · Min {stockText(product.min_stock)}{!product.active ? " · Nonaktif" : ""}</small><Button variant="outline" className="h-8 mt-2 text-xs" onClick={() => setAliasProduct(product)}>Alias Nota</Button></td>
                  {productData.stores.map((store) => <td key={store.id} className="p-3 text-right font-mono">{stockText(product.stock_by_store[store.id])}</td>)}
                  <td className="p-3 text-right font-mono font-bold">{stockText(product.total_stock)}</td>
                  <td className="p-3"><span>{product.last_purchase?.date || "—"}</span><small className="block text-ui-muted">{product.last_purchase ? `${product.last_purchase.qty ?? "—"} ${product.last_purchase.unit} · ${moneyText(product.last_purchase.cost)}` : "Belum ada rincian"}</small></td>
                </tr>)}
                {!productsLoading && !filteredProducts.length && <tr><td colSpan={(productData?.stores.length || 0) + 3} className="p-10 text-center text-ui-muted">{productSearch ? "Produk tidak ditemukan." : "Belum ada produk dari supplier ini."}</td></tr>}
                {productsLoading && <tr><td colSpan={(productData?.stores.length || 0) + 3} className="p-10 text-center text-ui-muted">Memuat produk supplier…</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap justify-between gap-3 items-center shrink-0">
            <span className="text-xs text-ui-muted">{filteredProducts.length} dari {productData?.products.length || 0} produk · Export mengikuti filter di atas</span>
            <Button variant="outline" onClick={() => setProductsOpen(false)}>Tutup</Button>
          </div>
        </DialogContent>
      </Dialog>
      {aliasProduct && productData?.supplier && <SupplierAliasDialog supplier={productData.supplier} productId={aliasProduct.id} onClose={() => setAliasProduct(null)} />}
    </>
  );
}
